import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { validateProjectName, PROJECT_NAME_MIN, PROJECT_NAME_MAX } from '@courseai/domain';
import type { ProjectRole } from '@courseai/domain';
import { useAuth } from '../auth/AuthContext';
import { useT, type TranslationKey } from '../i18n';
import {
  MY_PROJECTS,
  CREATE_PROJECT,
  type MyProjectsData,
  type ProjectListItem,
  type CreateProjectData,
  type CreateProjectVariables,
} from '../graphql/projects';

// Translation keys for the ProjectRole values so the list can show the current
// user's role in the project.
const ROLE_LABEL_KEYS: Record<ProjectRole, TranslationKey> = {
  owner: 'role.owner',
  editor: 'role.editor',
  viewer: 'role.viewer',
};

// Resolve the signed-in creator's role within a project from the membership
// list the API returns (Req 2.3). The API derives identity from the JWT; the
// same userId is exposed by the auth context for UI use only.
function roleForUser(
  project: ProjectListItem,
  userId: string | undefined,
): ProjectRole | undefined {
  if (userId === undefined) {
    return undefined;
  }
  return project.members.find((member) => member.userId === userId)?.role;
}

// Projects list screen: renders the creator's projects and offers a
// create-project form. Drives the list from `myProjects` and creation from
// `createProject` (Req 2.1, 2.2, 2.3, 2.4).
export function ProjectsList(): ReactNode {
  const auth = useAuth();
  const { t } = useT();
  const { data, loading, error, refetch } = useQuery<MyProjectsData>(MY_PROJECTS, {
    fetchPolicy: 'cache-and-network',
  });
  const [showForm, setShowForm] = useState(false);

  const projects = data?.myProjects ?? [];

  if (loading && data === undefined) {
    return (
      <section className="screen">
        <p>{t('projects.loading')}</p>
      </section>
    );
  }

  if (error) {
    return (
      <section className="screen">
        <div className="screen-header">
          <h2>{t('projects.title')}</h2>
          <button type="button" onClick={() => void refetch()}>
            {t('projects.retry')}
          </button>
        </div>
        <p className="form-error">{t('projects.loadError', { message: error.message })}</p>
      </section>
    );
  }

  return (
    <section className="screen">
      <div className="screen-header">
        <h2>{t('projects.title')}</h2>
        <button type="button" onClick={() => setShowForm(true)}>
          {t('projects.create')}
        </button>
      </div>

      {projects.length === 0 ? (
        <div className="empty-state">
          <p>{t('projects.emptyTitle')}</p>
          <p>{t('projects.emptyHint')}</p>
        </div>
      ) : (
        <ul className="project-list">
          {projects.map((project) => {
            const role = roleForUser(project, auth.user?.userId);
            return (
              <li key={project.projectId} className="project-card">
                <Link to={`/projects/${project.projectId}`} className="project-card-name">
                  {project.name}
                </Link>
                {project.description ? (
                  <p className="project-card-description">{project.description}</p>
                ) : (
                  <p className="project-card-description project-card-description-empty">
                    {t('projects.noDescription')}
                  </p>
                )}
                <span className="project-card-role">
                  {t(role ? ROLE_LABEL_KEYS[role] : 'role.member')}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {showForm && (
        <CreateProjectForm
          onClose={() => setShowForm(false)}
          onCreated={async () => {
            setShowForm(false);
            await refetch();
          }}
        />
      )}
    </section>
  );
}

interface CreateProjectFormProps {
  onClose(): void;
  onCreated(): Promise<void> | void;
}

// Modal create-project form. Validates the name client-side with the shared
// domain validator (1-100 chars, non-blank) before calling `createProject`, so
// invalid input never reaches the API and the user gets immediate feedback
// (Req 2.1, 2.2).
function CreateProjectForm({ onClose, onCreated }: CreateProjectFormProps): ReactNode {
  const { t } = useT();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [validationError, setValidationError] = useState<string | undefined>(undefined);

  const [createProject, { loading }] = useMutation<CreateProjectData, CreateProjectVariables>(
    CREATE_PROJECT,
  );
  const [submitError, setSubmitError] = useState<string | undefined>(undefined);

  const nameHint = t('projects.form.nameHint', {
    min: PROJECT_NAME_MIN,
    max: PROJECT_NAME_MAX,
  });

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setSubmitError(undefined);

    const result = validateProjectName(name);
    if (!result.ok) {
      // Present a localized message; the shared validator carries the field and
      // a human-readable message. We surface a localized equivalent.
      setValidationError(
        t('projects.form.nameInvalid', { min: PROJECT_NAME_MIN, max: PROJECT_NAME_MAX }),
      );
      return;
    }
    setValidationError(undefined);

    const trimmedDescription = description.trim();
    try {
      await createProject({
        variables: {
          input: {
            name,
            ...(trimmedDescription.length > 0 ? { description: trimmedDescription } : {}),
          },
        },
      });
      await onCreated();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : t('projects.form.createError'));
    }
  }

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={t('projects.form.title')}
    >
      <form className="modal-card" onSubmit={handleSubmit}>
        <h3>{t('projects.form.title')}</h3>
        <label>
          {t('projects.form.name')}
          <input
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (validationError) {
                setValidationError(undefined);
              }
            }}
            autoFocus
            required
          />
        </label>
        <p className="field-hint">{nameHint}</p>

        <label>
          {t('projects.form.description')}
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
        </label>

        {validationError && <p className="form-error">{validationError}</p>}
        {submitError && <p className="form-error">{submitError}</p>}

        <div className="modal-actions">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={loading}>
            {t('projects.form.cancel')}
          </button>
          <button type="submit" disabled={loading}>
            {loading ? t('projects.form.creating') : t('projects.form.create')}
          </button>
        </div>
      </form>
    </div>
  );
}
